import type { CustomCompType } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Tooltip } from '@neo/primitives/tooltip'
import { AlertCircle, FileIcon, UploadIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import { MAX_FILES_ALLOWED } from '../../config'
import type { NewKBFormValues } from '../../types'

interface FileListProps {
  files: RFileUploadResponse[]
  onRemove: (fileId: string) => void
}

const FileList = ({ files, onRemove }: FileListProps) => {
  if (files.length === 0) return null

  return (
    <div className="mt-4 space-y-2">
      {files.map((file) => (
        <div
          key={file.file_id}
          className="flex items-center justify-between p-3 bg-background rounded-md border"
          data-testid={`list-item-file-${file.file_id}`}
        >
          <div className="flex items-center gap-2 text-secondary">
            <FileIcon className="w-4 h-4 text-secondary" />
            <span className="text-xs-500 truncate max-w-[200px]" title={file.file_name}>
              {file.file_name}
            </span>
            <span className="text-xs-400">{(file.file_size / 1024).toFixed(2)} KB</span>
          </div>
          <Tooltip content="Remove file">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onRemove(file.file_id)}
              data-testid={`button-remove-file-${file.file_id}`}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </Tooltip>
        </div>
      ))}
    </div>
  )
}

interface FileUploadFieldProps {
  value: RFileUploadResponse[]
  onDrop: (files: File[]) => void
  onRemove: (fileId: string) => void
  isUploading?: boolean
  error?: string | null
  onError?: (msg: string | null) => void
}

const FileUploadField = ({
  value = [],
  onDrop,
  onRemove,
  isUploading = false,
  error = null,
  onError,
}: FileUploadFieldProps) => {
  const acceptedTypes = {
    'text/plain': ['.txt'],
    'application/json': ['.json'],
    'application/xml': ['.xml'],
    'text/csv': ['.csv'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  }

  const isMaxFilesReached = value.length >= MAX_FILES_ALLOWED

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      onError?.(null) // clear any prior error when a valid drop starts
      onDrop(accepted)
    },
    accept: acceptedTypes,
    disabled: isUploading || isMaxFilesReached,
    maxSize: 64 * 1024 * 1024, // 64MB
    multiple: true,
    // Show the reason inline (next to the dropzone) instead of a toast the user may miss.
    onDropRejected: () => {
      onError?.(
        'Unsupported file type or larger than 64MB. Allowed: CSV, PDF, DOCX, TXT, JSON, XML.',
      )
    },
  })

  const getUploadStatusText = () => {
    if (isMaxFilesReached) return `Maximum ${MAX_FILES_ALLOWED} files limit reached`
    if (isUploading) return 'Uploading...'
    if (isDragActive) return 'Drop files here...'
    return 'Drag files or click to upload'
  }

  return (
    <div className="space-y-4">
      {!isMaxFilesReached && (
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer transition-colors',
            isDragActive ? 'border-primary bg-primary/5' : 'hover:border-primary',
            isUploading && 'opacity-50 pointer-events-none',
          )}
          data-testid="dropzone-file-upload"
        >
          <input {...getInputProps()} data-testid="input-file-upload" />
          <div className="flex gap-2 text-tertiary items-center">
            <UploadIcon className="size-5" />
            <div className="text-left">
              <div className="text-xs-500">{getUploadStatusText()}</div>
              <div className="text-xs-400">
                Supports CSV, PDF, DOCX, TXT, JSON, XML. Max 64MB each.
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div
          className="flex items-start gap-1.5 text-xs-500 text-destructive"
          data-testid="file-upload-error"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <FileList files={value} onRemove={onRemove} />
    </div>
  )
}

export const FileUploadWrapper: CustomCompType<NewKBFormValues> = ({ value, onChange }) => {
  const [error, setError] = useState<string | null>(null)
  const { uploadFile, isUploading } = useFileUpload(
    () => {
      // We'll handle the form update in handleDrop instead
    },
    { headers: { channel: 'whatsapp' } },
  )

  const handleDrop = async (files: File[]) => {
    if (files?.length === 0) return
    setError(null)
    const currentFiles = (value as RFileUploadResponse[]) || []
    const remainingSlots = MAX_FILES_ALLOWED - currentFiles.length
    const filesToUpload = files.slice(0, remainingSlots)

    const uploadPromises = filesToUpload.map((file) => uploadFile(file))
    const uploadedFiles = (await Promise.all(uploadPromises)).filter(
      (file): file is RFileUploadResponse => file !== null,
    )
    onChange([...currentFiles, ...uploadedFiles])

    // Surface failures inline so they aren't missed: skipped-over-limit and per-file upload errors.
    if (files.length > remainingSlots) {
      setError(`Only ${MAX_FILES_ALLOWED} files allowed — extra file(s) were skipped.`)
    } else if (uploadedFiles.length < filesToUpload.length) {
      const failed = filesToUpload.length - uploadedFiles.length
      setError(`${failed} file(s) failed to upload. Please check the file and try again.`)
    }
  }

  const handleRemove = (fileId: string) => {
    const currentFiles = (value as RFileUploadResponse[]) || []
    onChange(currentFiles.filter((file) => file.file_id !== fileId))
  }

  return (
    <ScrollArea className="h-full">
      <div className="text-s-500 mb-2">Upload file</div>
      <FileUploadField
        value={(value as RFileUploadResponse[]) || []}
        onDrop={handleDrop}
        onRemove={handleRemove}
        isUploading={isUploading}
        error={error}
        onError={setError}
      />
    </ScrollArea>
  )
}
